
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import logo from '@/assets/logo_EMA.png';
import {
  Zap,
  LogOut,
  Shield,
  Users,
  FileText,
  Database,
  Bell,
  CreditCard,
  Settings,
  User,
  MessageCircle,
  TrendingUp,
  Lightbulb,
  Home,
  Activity,
  Gauge,
  Server,
  LayoutDashboard,
  Send,
  Building,
  DollarSign,
  Phone,
  HelpCircle,
  Thermometer,
  BookOpen,
  Crown,
  BarChart3,
  UserCheck,
  Building2,
  Wrench,
  LifeBuoy,
  KanbanSquare,
  LayoutGrid
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantView } from '../../contexts/TenantViewContext';
import { useActingAsAdmin } from '../../hooks/useActingAsAdmin';
import { supabase } from '@/integrations/supabase/client';

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { logout, isAdmin, isOwner, isConcierge, user, isPlatformAdminUser, ownedApartments, conciergeApartments } = useAuth();
  const { inTenantView } = useTenantView();
  const { setOpen } = useSidebar();
  const actingAsAdmin = useActingAsAdmin();
  const [hasUnsignedContract, setHasUnsignedContract] = useState(false);

  // Check for unsigned contracts for regular users
  useEffect(() => {
    const checkUnsignedContracts = async () => {
      if (!user || actingAsAdmin) return;

      try {
        const { data, error } = await supabase
          .from('contracts')
          .select('id, signed_at')
          .eq('user_id', user.id)
          .is('signed_at', null)
          .limit(1);

        if (!error && data && data.length > 0) {
          setHasUnsignedContract(true);
        } else {
          setHasUnsignedContract(false);
        }
      } catch (err) {
        console.error('Error checking unsigned contracts:', err);
      }
    };

    checkUnsignedContracts();
  }, [user?.id, actingAsAdmin]);

  const handleNavigation = (url: string) => {
    navigate(url);
    setOpen(false);
  };

  // Strip query string for active path comparison
  const urlPath = (url: string) => url.split('?')[0];

  const adminItems = [
    { key: "dashboard", title: t('nav.dashboard'), url: "/admin/dashboard", icon: LayoutDashboard },
    { key: "users", title: t('nav.userManagement'), url: "/admin/users", icon: Users },
    { key: "buildings", title: t('nav.buildings'), url: "/admin/buildings", icon: Building },
    { key: "building-pricing", title: t('nav.buildingPricing'), url: "/admin/building-pricing", icon: DollarSign },
    { key: "smart-meters", title: t('nav.smartMeters'), url: "/admin/smart-meters", icon: Gauge },
    { key: "contracts", title: t('nav.contractManagement'), url: "/admin/contracts", icon: FileText },
    { key: "notifyc_client", title: t('nav.clientNotifications'), url: "/admin/notifyc_client", icon: Send },
    { key: "access-control", title: t('nav.accessControl'), url: "/admin/access-control", icon: Shield },
    { key: "role-assignments", title: t('nav.roleAssignments'), url: "/admin/role-assignments", icon: UserCheck },
    { key: "topup-history", title: t('nav.topupHistory'), url: "/admin/topup-history", icon: CreditCard },
    { key: "reports", title: t('nav.reports'), url: "/admin/reports", icon: BarChart3 },
    { key: "documentation", title: t('nav.documentation'), url: "/documentation?tab=admin", icon: BookOpen },
  ];

  const superAdminItems = [
    { key: "superadmin", title: t('nav.superAdmins'), url: "/superadmin", icon: Crown },
    { key: "financial-logs", title: t('nav.financialLogs'), url: "/superadmin/financial-logs", icon: CreditCard },
    { key: "activity-logs", title: t('nav.activityLogs'), url: "/superadmin/activity-logs", icon: Activity },
    { key: "bill-reports", title: t('nav.billReports'), url: "/superadmin/bill-reports", icon: BarChart3 },
    { key: "meter-operations", title: t('nav.meterOperations'), url: "/superadmin/meter-operations", icon: Wrench },
    { key: "support-tickets", title: t('nav.supportTickets'), url: "/superadmin/support-tickets", icon: LifeBuoy },
    { key: "sprint-board", title: t('nav.sprintBoard'), url: "/superadmin/sprint-board", icon: KanbanSquare },
    { key: "tools", title: t('nav.tools'), url: "/superadmin/tools", icon: LayoutGrid },
    { key: "settings", title: t('nav.settings'), url: "/superadmin/settings", icon: Settings },
  ];

  const userMenuItems = [
    { key: "dashboard", title: t('nav.dashboard'), url: "/user/dashboard", icon: Home },
    { key: "notices", title: t('nav.notices'), url: "/user/notices", icon: Bell },
    { key: "bills", title: t('nav.topupHistory'), url: "/user/bills", icon: CreditCard },
    { key: "contracts", title: t('nav.contracts'), url: "/user/contracts", icon: FileText },
    { key: "chatbot", title: t('nav.chatbot'), url: "/user/chatbot", icon: MessageCircle },
    { key: "consumption-evolution", title: t('nav.consumptionEvolution'), url: "/user/consumption-evolution", icon: TrendingUp },
    { key: "topup", title: t('nav.topUp'), url: "/user/topup", icon: CreditCard },
    { key: "contact-support", title: t('nav.contactSupport'), url: "/user/contact-support", icon: Phone },
    { key: "documentation", title: t('nav.documentation'), url: "/documentation?tab=user", icon: BookOpen },
    { key: "settings", title: t('nav.profileSettings'), url: "/user/settings", icon: Settings },
  ];

  const ownerMenuItems = [
    { key: "dashboard", title: t('nav.dashboard'), url: "/owner/dashboard", icon: LayoutDashboard },
    { key: "units", title: t('nav.myUnits'), url: "/owner/units", icon: Building2 },
    { key: "assign-roles", title: t('nav.assignRoles'), url: "/owner/assign-roles", icon: Users },
    { key: "bills", title: t('nav.bills'), url: "/owner/bills", icon: CreditCard },
    { key: "topup", title: t('nav.topUp'), url: "/owner/topup", icon: Zap },
    { key: "topup-history", title: t('nav.topupHistory'), url: "/owner/topup-history", icon: CreditCard },
    { key: "contracts", title: t('nav.contracts'), url: "/owner/contracts", icon: FileText },
    { key: "documentation", title: t('nav.documentation'), url: "/documentation?tab=owner", icon: BookOpen },
    { key: "settings", title: t('nav.settings'), url: "/owner/settings", icon: Settings },
  ];

  const conciergeMenuItems = [
    { key: "dashboard", title: t('nav.dashboard'), url: "/concierge/dashboard", icon: LayoutDashboard },
    { key: "apartments", title: t('nav.myApartments'), url: "/concierge/apartments", icon: Building2 },
    { key: "topup", title: t('nav.topUp'), url: "/concierge/topup", icon: Zap },
    { key: "topup-history", title: t('nav.topupHistory'), url: "/concierge/topup-history", icon: CreditCard },
    { key: "documentation", title: t('nav.documentation'), url: "/documentation?tab=concierge", icon: BookOpen },
    { key: "settings", title: t('nav.settings'), url: "/concierge/settings", icon: Settings },
  ];

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center space-x-3">
          <img src={logo} alt="Growth Energy" className="w-10 h-10 object-contain" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              {actingAsAdmin ? t('nav.adminPanel') : !inTenantView && isOwner() ? t('nav.ownerPortal') : !inTenantView && isConcierge() ? t('nav.conciergePortal') : t('common.appName')}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
              {t('common.appTagline')}
            </span>
            {isPlatformAdminUser() && (
              <Badge variant="outline" className="mt-1 w-fit bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-700">
                <Crown size={12} className="mr-1" />
                {t('nav.superAdmin')}
              </Badge>
            )}
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {actingAsAdmin ? (
          <>
            {isPlatformAdminUser() && (
              <SidebarGroup>
                <SidebarGroupLabel className="text-purple-600">{t('nav.superAdmin')}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {superAdminItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          isActive={location.pathname === urlPath(item.url)}
                          className="data-[active=true]:bg-purple-100 data-[active=true]:text-purple-800 hover:bg-purple-50 hover:text-purple-700"
                          onClick={() => handleNavigation(item.url)}
                        >
                          <item.icon size={20} />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
            <SidebarGroup>
              <SidebarGroupLabel className="text-red-600">{t('nav.administration')}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {adminItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        isActive={location.pathname === urlPath(item.url)}
                        className="data-[active=true]:bg-red-100 data-[active=true]:text-red-800 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon size={20} />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        ) : !inTenantView && isOwner() ? (
          <SidebarGroup>
            <SidebarGroupLabel className="text-blue-600">{t('nav.ownerPortal')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {ownerMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url) || location.pathname.startsWith(urlPath(item.url) + '/')}
                      className="data-[active=true]:bg-blue-100 data-[active=true]:text-blue-800 hover:bg-blue-50 hover:text-blue-700"
                      onClick={() => handleNavigation(item.url)}
                    >
                      <item.icon size={20} />
                      <span>{item.title}</span>
                      {item.key === "units" && ownedApartments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {ownedApartments.length}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : !inTenantView && isConcierge() ? (
          <SidebarGroup>
            <SidebarGroupLabel className="text-teal-600">{t('nav.conciergePortal')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {conciergeMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url) || location.pathname.startsWith(urlPath(item.url) + '/')}
                      className="data-[active=true]:bg-teal-100 data-[active=true]:text-teal-800 hover:bg-teal-50 hover:text-teal-700"
                      onClick={() => handleNavigation(item.url)}
                    >
                      <item.icon size={20} />
                      <span>{item.title}</span>
                      {item.key === "apartments" && conciergeApartments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {conciergeApartments.length}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupLabel>{t('nav.mainMenu')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {userMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname === urlPath(item.url)}
                      onClick={() => handleNavigation(item.url)}
                    >
                      <div className="relative">
                        <item.icon size={20} />
                        {item.key === "contracts" && hasUnsignedContract && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                        )}
                      </div>
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout}>
              <LogOut size={20} />
              <span className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                {t('header.signOut')}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
